import React, { useState } from 'react';
import {
  Dialog,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUserAccounts } from '../../store/actions/accountActions';
import AccountDetails from './steps/AccountDetails';
import AddressDetails from './steps/AddressDetails';
import SubscriptionPlan from './steps/SubscriptionPlan';
import PaymentSetup from './steps/PaymentSetup';
import ReviewDetails from './steps/ReviewDetails';
import { PaymentElementsProvider } from './steps/PaymentSetup';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const steps = [
  'Account Details',
  'Address Information',
  'Choose Plan',
  'Payment Setup',
  'Review',
];

function AccountSetupWizard({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    accountDetails: {
      name: '',
      type: 'business',
    },
    addresses: {
      billing: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
      },
      shipping: {
        sameAsBilling: true,
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
      },
    },
    subscription: {
      plan: '',
      interval: 'month',
    },
    paymentMethod: null,
  });

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleStepChange = (data) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
    handleNext();
  };

  const handleSubmit = async (stripe, elements) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Create account and set up subscription
      const response = await fetch('/api/accounts/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create account');
      }

      // Create a PaymentElement for confirmation
      return (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: data.clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#5c2d91',
                colorBackground: '#ffffff',
                colorText: '#30313d',
              },
            },
          }}
        >
          {async () => {
            // Confirm the subscription payment
            const { error: paymentError } = await stripe.confirmPayment({
              elements,
              clientSecret: data.clientSecret,
              confirmParams: {
                return_url: `${window.location.origin}/dashboard`,
              },
            });

            if (paymentError) {
              throw new Error(paymentError.message);
            }

            // Refresh accounts list
            await dispatch(fetchUserAccounts());

            // Show success message and redirect
            onClose();
            navigate('/dashboard', { 
              state: { 
                message: 'Account created successfully!',
                accountId: data.account.id 
              }
            });
          }}
        </Elements>
      );

    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <AccountDetails
            data={formData.accountDetails}
            onNext={handleStepChange}
          />
        );
      case 1:
        return (
          <AddressDetails
            data={formData.addresses}
            onNext={handleStepChange}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <SubscriptionPlan
            data={formData.subscription}
            onNext={handleStepChange}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <PaymentSetup
            data={formData}
            onNext={handleStepChange}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <PaymentElementsProvider clientSecret={formData.paymentMethod?.clientSecret}>
            <ReviewDetails
              data={formData}
              onSubmit={handleSubmit}
              onBack={handleBack}
            />
          </PaymentElementsProvider>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Create New Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {isSubmitting ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            py: 4
          }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Creating your account...</Typography>
          </Box>
        ) : (
          getStepContent(activeStep)
        )}
      </Box>
    </Dialog>
  );
}

export default AccountSetupWizard; 